import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, Zap, AlertCircle, Sparkles } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/hooks/use-toast';
import { EmptyPlaceholder } from '@/components/EmptyPlaceholder';
import { useAuth } from '@/context/auth';
import { hasSuperAdminAccess, hasOrgAdminAccess, hasRestAdminAccess } from '@/context/auth/types';
import { EmailTriggerForm, EmailTriggerList } from '@/components/email/triggers';
import EmailTriggerTemplates from '@/components/email/triggers/EmailTriggerTemplates';
import EmailTriggerService, { EmailTriggerRule } from '@/api/emailTriggers';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import type { EmailTriggerTemplate } from '@/types/email-triggers';

interface TriggersTabProps {
  onError?: (error: Error) => void;
}

type ViewMode = 'list' | 'create' | 'edit' | 'templates';

const TriggersTab: React.FC<TriggersTabProps> = ({ onError }) => {
  const { user } = useAuth();
  const [currentView, setCurrentView] = useState<ViewMode>('list');
  const [selectedTriggerId, setSelectedTriggerId] = useState<string | null>(null);
  const [triggers, setTriggers] = useState<EmailTriggerRule[]>([]);
  const [loading, setLoading] = useState(false);

  // Check if user can create triggers
  const canCreateTrigger = user && (
    hasSuperAdminAccess(user.role) ||
    hasOrgAdminAccess(user.role) ||
    hasRestAdminAccess(user.role)
  );

  // Fetch triggers from API
  const fetchTriggers = async () => {
    if (!user?.organisationId) return;

    setLoading(true);
    try {
      const data = await EmailTriggerService.fetchTriggers(user.organisationId);
      setTriggers(data);
    } catch (error: any) {
      console.error('Error fetching triggers:', error);
      toast({
        title: 'Error',
        description: 'Failed to load triggers',
        variant: 'destructive'
      });
      if (onError) onError(error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch triggers on mount and when user changes
  useEffect(() => {
    fetchTriggers();
  }, [user?.organisationId]);

  // View transition handlers
  const handleCreateTrigger = () => {
    setCurrentView('create');
    setSelectedTriggerId(null);
  };

  const handleBrowseTemplates = () => {
    setCurrentView('templates');
  };

  const handleTemplateSelect = (template?: EmailTriggerTemplate) => {
    // Template will be loaded from localStorage in EmailTriggerForm
    setCurrentView('create');
    setSelectedTriggerId(null);
  };

  const handleCreateBlank = () => {
    // Clear any template selection
    localStorage.removeItem('selectedEmailTriggerTemplate');
    setCurrentView('create');
    setSelectedTriggerId(null);
  };

  const handleEditTrigger = (triggerId: string) => {
    setSelectedTriggerId(triggerId);
    setCurrentView('edit');
  };

  const handleBackToList = () => {
    setCurrentView('list');
    setSelectedTriggerId(null);
  };

  const handleTriggerSaved = () => {
    toast({
      title: 'Success',
      description: 'Email trigger saved successfully',
    });
    handleBackToList();
    fetchTriggers(); // Refresh the list
  };

  // Render templates view
  if (currentView === 'templates') {
    return (
      <div className="py-4 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h3 className="text-lg font-medium">Email Trigger Templates</h3>
            <p className="text-sm text-muted-foreground">
              Choose from pre-built templates or start from scratch
            </p>
          </div>
          <Button variant="outline" onClick={handleBackToList}>
            Back to Triggers
          </Button>
        </div>

        <Separator />

        <EmailTriggerTemplates
          onTemplateSelect={handleTemplateSelect}
          onCreateBlank={handleCreateBlank}
        />
      </div>
    );
  }

  // Render create/edit form
  if (currentView === 'create' || currentView === 'edit') {
    return (
      <div className="py-4 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h3 className="text-lg font-medium">
              {currentView === 'create' ? 'Create Email Trigger' : 'Edit Email Trigger'}
            </h3>
            <p className="text-sm text-muted-foreground">
              {currentView === 'create'
                ? 'Set up automated emails based on customer behavior'
                : 'Update your automated email trigger settings'
              }
            </p>
          </div>
          <Button variant="outline" onClick={handleBackToList}>
            Back to Triggers
          </Button>
        </div>

        <Separator />

        <EmailTriggerForm
          triggerId={selectedTriggerId}
          onSuccess={handleTriggerSaved}
          onCancel={handleBackToList}
        />
      </div>
    );
  }

  // Render list view
  return (
    <div className="space-y-6">
      {/* Header with buttons - always show */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Triggers</h3>
        {canCreateTrigger && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleBrowseTemplates}>
              Templates
            </Button>
            <Button onClick={handleCreateTrigger}>
              <Plus className="mr-2 h-4 w-4" />
              Create Trigger
            </Button>
          </div>
        )}
      </div>

      {/* Empty state or trigger list */}
      {triggers.length === 0 && !loading ? (
        <div className="space-y-8">
          {/* Empty state */}
          <div className="text-center space-y-4 py-12">
            <div className="flex justify-center">
              <Zap className="h-12 w-12 text-muted-foreground" />
            </div>
            <div className="space-y-2">
              <h3 className="text-xl font-semibold">No email triggers yet</h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                Create automated email triggers based on customer behavior and milestones
              </p>
            </div>
            {canCreateTrigger && (
              <div className="flex justify-center gap-3 pt-4">
                <Button onClick={handleBrowseTemplates} size="lg" variant="outline">
                  <Sparkles className="mr-2 h-4 w-4" />
                  Browse Templates
                </Button>
                <Button onClick={handleCreateTrigger} size="lg">
                  Create Trigger
                </Button>
              </div>
            )}
          </div>

          {/* Why Use Email Triggers section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Why Use Email Triggers?</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-6">
                  <h4 className="font-semibold mb-2">Automated Engagement</h4>
                  <p className="text-sm text-muted-foreground">
                    Send personalized emails automatically based on customer actions and milestones.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <h4 className="font-semibold mb-2">Increase Revenue</h4>
                  <p className="text-sm text-muted-foreground">
                    Re-engage dormant customers and encourage repeat orders with targeted messaging.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <h4 className="font-semibold mb-2">Save Time</h4>
                  <p className="text-sm text-muted-foreground">
                    Set up once and let triggers run automatically, reaching customers at the right time.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      ) : (
        <EmailTriggerList
          triggers={triggers}
          loading={loading}
          onEdit={handleEditTrigger}
          onRefresh={fetchTriggers}
        />
      )}
    </div>
  );
};

export default TriggersTab;
